import React, { useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { Field, getFormValues, reduxForm } from 'redux-form';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import renderRadioButtonField from '../../../shared/components/form/RadioButton';
import renderSelectField from '../../../shared/components/form/Select';
import renderSwitchField from '../../../shared/components/custom/Switch';
import t, { partial } from '../../../util/translation/translation';
import validate from './validateAccess';

const o = partial('OfferForm');
const s = partial('shared');
const e = partial('EventsForm');
const n = partial('NewsForm');

const ChatAccessForm = (props) => {
  const {
    handleSubmit,
    initialize,
    groupsLevelsTiers,
    access,
    accessFormValues = {},
    change,
  } = props;
  const { tiers, levels, groups } = groupsLevelsTiers;
  const initializeRef = useRef(initialize);
  const targetAudience = useMemo(
    () => accessFormValues?.targetAudience,
    [accessFormValues]
  );

  useEffect(() => {
    initializeRef.current = initialize;
  }, [initialize]);

  useEffect(() => {
    const getTargetAudience = () => {
      if (access.levels || access.groups || access.tiers) {
        return 'targetAudience';
      }
      return 'all';
    };

    const getSelectedFields = (
      accessObject,
      objectField,
      title,
      searchArray
    ) => {
      if (Array.isArray(accessObject[objectField])) {
        if (accessObject[objectField].length === 0) {
          return [{ label: e(`all${title}`), value: [] }];
        }

        return accessObject[objectField]
          .map((accessItem) =>
            searchArray.find((item) => item.value === accessItem)
          )
          .filter(Boolean);
      }

      return [{ label: s('ignore'), value: null }];
    };

    initializeRef.current({
      targetAudience: getTargetAudience(),
      level: getSelectedFields(access, 'levels', 'Levels', levels),
      tier: getSelectedFields(access, 'tiers', 'Tiers', tiers),
      group: getSelectedFields(access, 'groups', 'Groups', groups),
      noAccessView:
        typeof access.noAccessView === 'boolean' ? access.noAccessView : true,
      visible: typeof access.visible === 'boolean' ? access.visible : true,
    });
  }, [access, levels, groups, tiers]);

  useEffect(() => {
    const adjustDropdowns = (fieldName, fieldValues) => {
      if (fieldValues && fieldValues.length > 1) {
        const [old, ...newOnes] = fieldValues;
        const newestOne = fieldValues[fieldValues.length - 1];

        if (old.value === null || Array.isArray(old.value)) {
          change(fieldName, newOnes);
        }
        if (newestOne.value === null || Array.isArray(newestOne.value)) {
          change(fieldName, [newestOne]);
        }
      }
    };

    adjustDropdowns('group', accessFormValues.group);
    adjustDropdowns('level', accessFormValues.level);
    adjustDropdowns('tier', accessFormValues.tier);
  }, [
    accessFormValues.level,
    accessFormValues.group,
    accessFormValues.tier,
    change,
  ]);

  const handleAudienceChange = (...args) => {
    const newAudience = args.find(
      (arg) => arg === 'all' || arg === 'targetAudience'
    );
    const ignore = [{ label: s('ignore'), value: null }];

    if (!newAudience) {
      return;
    }

    if (newAudience === 'all') {
      change('level', ignore);
      change('tier', ignore);
      change('group', ignore);
    } else {
      change('level', [{ label: e('allLevels'), value: [] }]);
      change('tier', [{ label: e('allTiers'), value: [] }]);
      change('group', [{ label: e('allGroups'), value: [] }]);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div
        style={{
          backgroundColor: '#F4F4F6',
          borderRadius: 4,
          borderColor: '#D7DAE0',
          borderStyle: 'solid',
          borderWidth: 1,
          marginTop: 15,
          marginBottom: 15,
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomStyle: 'dashed',
            borderColor: '#D7DAE0',
            padding: '10px 10px 10px 10px',
          }}
        >
          <div
            style={{
              fontSize: 16,
              lineHeight: '24px',
              color: 'black',
              fontWeight: 'bold',
            }}
          >
            {s('access')}
          </div>
        </div>
        <div style={{ padding: '10px 10px 10px 10px' }}>
          <Col>
            <Row>
              <Col>
                <span className="form">{n('targetAudienceTitle')}</span>
                <div className="form__form-group-field">
                  <div className="form__form-group">
                    <Field
                      name="targetAudience"
                      component={renderRadioButtonField}
                      radioValue="all"
                      label={n('targetAudience-all')}
                      defaultChecked
                      onChange={handleAudienceChange}
                    />
                    <Field
                      name="targetAudience"
                      label={n('targetAudience-custom')}
                      component={renderRadioButtonField}
                      radioValue="targetAudience"
                      onChange={handleAudienceChange}
                    />
                  </div>
                </div>
              </Col>
              <Col>
                <span className="form">
                  {n('noAccessView')}
                  &nbsp;
                  <TooltipWithIcon
                    id="chat-no-access-view-tooltip"
                    text={t('NewsFormPage.noAccessViewInfo')}
                  />
                </span>
                <div className="form__form-group-field">
                  <div className="form__form-group">
                    <Field
                      name="noAccessView"
                      component={renderRadioButtonField}
                      radioValue={true}
                      label={s('all')}
                      defaultChecked
                    />
                    <Field
                      name="noAccessView"
                      label={o('targetAudience')}
                      component={renderRadioButtonField}
                      radioValue={false}
                    />
                  </div>
                </div>
              </Col>
              <Col>
                <span className="form-header">{s('visible')}</span>
                <div className="form__form-group flex-Vcenter">
                  <Field
                    name="visible"
                    component={renderSwitchField}
                    label={[`${s('active')}`, `${s('inActive')}`]}
                    defaultChecked
                  />
                </div>
              </Col>
            </Row>
            {targetAudience !== 'all' ? (
              <Row>
                <Col>
                  <div className="form__form-group">
                    <span className="form-header">{o('partnerlevelList')}</span>
                    <Field
                      name="level"
                      placeholder={o('selectLevels')}
                      options={levels}
                      component={renderSelectField}
                      multiple
                    />
                  </div>
                </Col>
                <Col>
                  <div className="form__form-group">
                    <span className="form-header">{o('partnergroupList')}</span>
                    <Field
                      name="group"
                      placeholder={o('selectGroup')}
                      options={groups}
                      component={renderSelectField}
                      multiple
                    />
                  </div>
                </Col>
                <Col>
                  <div className="form__form-group">
                    <span className="form-header">{s('userTiers')}</span>
                    <Field
                      name="tier"
                      placeholder={o('selectTier')}
                      options={tiers}
                      component={renderSelectField}
                      multiple
                    />
                  </div>
                </Col>
              </Row>
            ) : null}
          </Col>
        </div>
      </div>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'chat_access_form',
  validate,
});

const mapStateToProps = (state) => ({
  accessFormValues: getFormValues('chat_access_form')(state),
});

export default connect(mapStateToProps)(reduxF(ChatAccessForm));
